/**
 * Typed fetch wrapper that the rest of the app uses for every API call.
 *
 * - Auth: relies on httpOnly cookies set by the backend (no token in JS).
 *   `credentials: 'include'` makes the cookie travel cross-origin in qa/prod
 *   builds. In dev the Vite proxy keeps everything same-origin.
 * - Errors: ALL failures throw an `ApiError` with a stable shape. UI can
 *   discriminate on `.status` / `.code` without prodding `.message`.
 * - Retries: idempotent verbs only, on transport errors + 502/503/504.
 *   We never retry POST/PUT/DELETE silently (idempotency keys are caller-
 *   driven; see `lib/idempotency.ts`).
 * - No global state: every request stands alone, no shared abort controller,
 *   no implicit cancellation.
 */

import { config } from './config'

export type Json = Record<string, unknown> | unknown[] | string | number | boolean | null

export class ApiError extends Error {
  status: number
  code: string | undefined
  /** Backend field name that failed validation — use for inline form errors. */
  field: string | undefined
  details: unknown
  requestId: string | undefined

  constructor(opts: {
    status: number
    message: string
    code?: string | undefined
    field?: string | undefined
    details?: unknown
    requestId?: string | undefined
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.status = opts.status
    this.code = opts.code
    this.field = opts.field
    this.details = opts.details
    this.requestId = opts.requestId
  }

  get isAuthError(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNetwork(): boolean {
    return this.status === 0
  }

  get isServer(): boolean {
    return this.status >= 500
  }

  get isValidation(): boolean {
    return this.status === 400 || this.status === 422
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: Json | FormData | undefined
  params?: Record<string, string | number | boolean | undefined> | undefined
  signal?: AbortSignal | undefined
  /** Per-request idempotency key (POST creates). */
  idempotencyKey?: string | undefined
  /** Skip retry on transient failures. */
  noRetry?: boolean | undefined
  /** Override default Accept header. */
  accept?: string | undefined
}

const RETRY_STATUSES = new Set([502, 503, 504])
const RETRY_METHODS = new Set(['GET', 'HEAD'])
const MAX_RETRIES = 2

// Shared refresh promise — if multiple requests 401 at the same time, they all
// wait on the same single refresh call instead of hammering /auth/refresh.
let _refreshPromise: Promise<void> | null = null

async function refreshAccessToken(): Promise<void> {
  const base = config.API_BASE_URL.replace(/\/$/, '')
  const res = await fetch(`${base}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    // Refresh token expired or missing — send user to login.
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError({ status: 401, message: 'Session expired. Please log in again.' })
  }
}

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const base = config.API_BASE_URL.replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  let url = `${base}${cleanPath}`
  if (params && Object.keys(params).length) {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') search.set(k, String(v))
    }
    const qs = search.toString()
    if (qs) url += `?${qs}`
  }
  return url
}

async function parseError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get('x-request-id') ?? undefined
  let body: unknown = undefined
  const contentType = response.headers.get('content-type') ?? ''
  try {
    if (contentType.includes('application/json')) body = await response.json()
    else body = await response.text()
  } catch {
    // Body was not parseable. Leave undefined.
  }

  const bodyObj =
    body && typeof body === 'object' && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : {}

  let message: string
  let code: string | undefined
  let field: string | undefined

  const detail = bodyObj.detail

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    // Our AppError format: {"detail": {"code", "field", "message", "request_id"}}
    const d = detail as Record<string, unknown>
    message =
      typeof d.message === 'string'
        ? d.message
        : response.statusText ?? `Request failed (${response.status})`
    code = typeof d.code === 'string' ? d.code : undefined
    field = typeof d.field === 'string' ? d.field : undefined
  } else if (Array.isArray(detail)) {
    // FastAPI / Pydantic 422: [{"loc": [..., "field"], "msg": "...", "type": "..."}]
    const first = detail[0] as Record<string, unknown> | undefined
    message = typeof first?.msg === 'string' ? first.msg : `Validation error`
    const loc = Array.isArray(first?.loc) ? (first.loc as unknown[]) : []
    field = loc.length > 1 ? String(loc[loc.length - 1]) : undefined
    code = typeof first?.type === 'string' ? first.type : undefined
  } else if (typeof detail === 'string') {
    message = detail
    code = typeof bodyObj.code === 'string' ? bodyObj.code : undefined
  } else {
    message =
      typeof bodyObj.message === 'string'
        ? bodyObj.message
        : response.statusText ?? `Request failed (${response.status})`
    code = typeof bodyObj.code === 'string' ? bodyObj.code : undefined
  }

  return new ApiError({
    status: response.status,
    message,
    code,
    field,
    details: body,
    requestId,
  })
}

async function doFetch(url: string, init: RequestInit, attempt = 0): Promise<Response> {
  try {
    const response = await fetch(url, init)
    if (
      RETRY_STATUSES.has(response.status) &&
      RETRY_METHODS.has((init.method ?? 'GET').toUpperCase()) &&
      attempt < MAX_RETRIES
    ) {
      await sleep(2 ** attempt * 150 + Math.random() * 100)
      return doFetch(url, init, attempt + 1)
    }
    return response
  } catch (err) {
    // Transport error — only retry idempotent methods.
    if (
      RETRY_METHODS.has((init.method ?? 'GET').toUpperCase()) &&
      attempt < MAX_RETRIES
    ) {
      await sleep(2 ** attempt * 150 + Math.random() * 100)
      return doFetch(url, init, attempt + 1)
    }
    throw new ApiError({
      status: 0,
      message: err instanceof Error ? err.message : 'Network error',
    })
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const url = buildUrl(path, options.params)

  const headers: Record<string, string> = {
    Accept: options.accept ?? 'application/json',
  }
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

  let body: BodyInit | undefined
  if (options.body !== undefined) {
    if (options.body instanceof FormData) {
      body = options.body
      // Let fetch set the multipart boundary.
    } else {
      body = JSON.stringify(options.body)
      headers['Content-Type'] = 'application/json'
    }
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  }

  const response = await doFetch(url, init, options.noRetry ? MAX_RETRIES : 0)

  if (response.status === 204) return undefined as T

  // Silently refresh the access token on 401 and retry once.
  // Skip the intercept when we ARE the refresh call to avoid an infinite loop.
  if (response.status === 401 && path !== '/auth/refresh') {
    if (!_refreshPromise) {
      _refreshPromise = refreshAccessToken().finally(() => {
        _refreshPromise = null
      })
    }
    try {
      await _refreshPromise
    } catch {
      // refreshAccessToken already navigated to /login; re-throw original error.
      throw await parseError(response)
    }
    // Retry the original request with the fresh cookie.
    const retried = await doFetch(url, init, MAX_RETRIES)
    if (retried.status === 204) return undefined as T
    if (!retried.ok) throw await parseError(retried)
    const retriedContentType = retried.headers.get('content-type') ?? ''
    if (retriedContentType.includes('application/json')) return (await retried.json()) as T
    return (await retried.text()) as T
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return (await response.json()) as T
  }
  return (await response.text()) as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: Json | FormData, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: Json | FormData, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: Json | FormData, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
